/**
 * PayrollSetupWizard — 4-step guided flow.
 *
 * Step 1: Anchor  — how do you talk about salaries?
 * Step 2: Pack    — starter template (or start from scratch).
 * Step 3: Customize — tick/edit each component, add your own.
 * Step 4: Preview + confirm.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkle, CheckCircle, ArrowLeft, Plus, Trash, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const ANCHORS = [
  { key:"ctc", icon:"💼", label:"Annual CTC", example:"₹18,00,000 / year",
    hint:"You promise a total cost-to-company. India's default for full-timers." },
  { key:"monthly_gross", icon:"📅", label:"Monthly Gross", example:"₹1,50,000 / month",
    hint:"You promise a fixed monthly amount before deductions. Common for consultants." },
  { key:"monthly_basic", icon:"🏦", label:"Monthly Basic", example:"₹60,000 / month",
    hint:"Legacy — you promise just the basic, allowances stack on top." },
  { key:"monthly_takehome", icon:"💰", label:"Monthly Take-home", example:"₹1,20,000 / month",
    hint:"You promise a specific net-in-hand amount. We solve backwards to compute CTC & components." },
];

const PACK_ICONS = { fte:"📋", contractor:"🧑‍💻", intern:"🎓", custom:"🛠️" };
const CUSTOM_PACK = {
  code: "custom", label: "Start from scratch",
  description: "Blank slate — you'll pick each component yourself in the next step.",
  components: [],
};

// Pack default lines mirror the backend PACKS.lines_by_anchor for the preview
// (so the customize step opens pre-populated).
const PACK_LINES_BY_ANCHOR = {
  fte: {
    ctc: [
      ["BASIC","earning","pct_of_ctc",50],["HRA","earning","pct_of_basic",40],
      ["CONV","earning","fixed_monthly",1600],["MEDICAL","earning","fixed_monthly",1250],
      ["LTA","earning","pct_of_ctc",5],["SPECIAL","earning","balance",0],
      ["PF","deduction","statutory",0],["EMPF","employer_cost","statutory",0],
      ["EPS","employer_cost","statutory",0],["EDLI","employer_cost","statutory",0],
      ["PF_ADMIN","employer_cost","statutory",0],
      ["ESIC","deduction","statutory",0],["EESIC","employer_cost","statutory",0],
      ["PT","deduction","statutory",0],["LWF","deduction","statutory",0],
      ["ELWF","employer_cost","statutory",0],
      ["GRAT","employer_cost","statutory",0],["TDS","deduction","statutory",0],
    ],
    monthly_gross: [
      ["BASIC","earning","pct_of_gross",50],["HRA","earning","pct_of_basic",40],
      ["CONV","earning","fixed_monthly",1600],["MEDICAL","earning","fixed_monthly",1250],
      ["SPECIAL","earning","balance",0],
      ["PF","deduction","statutory",0],["EMPF","employer_cost","statutory",0],
      ["EPS","employer_cost","statutory",0],["EDLI","employer_cost","statutory",0],
      ["PF_ADMIN","employer_cost","statutory",0],
      ["ESIC","deduction","statutory",0],["EESIC","employer_cost","statutory",0],
      ["PT","deduction","statutory",0],["LWF","deduction","statutory",0],
      ["ELWF","employer_cost","statutory",0],
      ["GRAT","employer_cost","statutory",0],["TDS","deduction","statutory",0],
    ],
    monthly_basic: [
      ["BASIC","earning","fixed_monthly",0],["HRA","earning","pct_of_basic",40],
      ["CONV","earning","fixed_monthly",1600],["MEDICAL","earning","fixed_monthly",1250],
      ["PF","deduction","statutory",0],["EMPF","employer_cost","statutory",0],
      ["EPS","employer_cost","statutory",0],["EDLI","employer_cost","statutory",0],
      ["PF_ADMIN","employer_cost","statutory",0],
      ["ESIC","deduction","statutory",0],["EESIC","employer_cost","statutory",0],
      ["PT","deduction","statutory",0],
      ["GRAT","employer_cost","statutory",0],["TDS","deduction","statutory",0],
    ],
  },
  contractor: {
    ctc: [["BASIC","earning","pct_of_ctc",100],["PT","deduction","statutory",0],["TDS","deduction","statutory",0]],
    monthly_gross: [["BASIC","earning","pct_of_gross",100],["PT","deduction","statutory",0],["TDS","deduction","statutory",0]],
    monthly_basic: [["BASIC","earning","fixed_monthly",0],["PT","deduction","statutory",0],["TDS","deduction","statutory",0]],
  },
  intern: {
    ctc: [["STIPEND","earning","pct_of_ctc",100],["TDS","deduction","statutory",0]],
    monthly_gross: [["STIPEND","earning","pct_of_gross",100],["TDS","deduction","statutory",0]],
    monthly_basic: [["STIPEND","earning","fixed_monthly",0],["TDS","deduction","statutory",0]],
  },
  custom: { ctc: [], monthly_gross: [], monthly_basic: [] },
};
// Aliases so take-home shares the gross template
["fte","contractor","intern"].forEach(p => {
  PACK_LINES_BY_ANCHOR[p].monthly_takehome = PACK_LINES_BY_ANCHOR[p].monthly_gross;
});
PACK_LINES_BY_ANCHOR.custom.monthly_takehome = [];

const CALC_LABEL = {
  fixed_monthly: "Fixed ₹/mo",
  pct_of_basic: "% of Basic",
  pct_of_ctc: "% of monthly CTC",
  pct_of_gross: "% of Gross",
  balance: "Balance (residual)",
  statutory: "Statutory (auto)",
};
const CALC_OPTIONS = ["fixed_monthly","pct_of_basic","pct_of_ctc","pct_of_gross","balance","statutory"];

const KIND_LABEL = { earning:"Earning", deduction:"Deduction", employer_cost:"Employer cost" };

export default function PayrollSetupWizard({ open, onOpenChange, onComplete }) {
  const [step, setStep] = useState(1);
  const [anchor, setAnchor] = useState("ctc");
  const [pack, setPack] = useState("fte");
  const [target, setTarget] = useState(1800000);
  const [packs, setPacks] = useState({});
  const [busy, setBusy] = useState(false);
  const [customLines, setCustomLines] = useState([]);

  useEffect(() => {
    if (open) {
      setStep(1); setAnchor("ctc"); setPack("fte"); setTarget(1800000);
      // Seed customLines inline (the pack/anchor effect below won't refire
      // if the values match the previous open).
      const template = (PACK_LINES_BY_ANCHOR["fte"] || {})["ctc"] || [];
      setCustomLines(template.map(([code, kind, calc, val]) => ({
        code, kind, calculation_type: calc, value: val, include: true, name: prettyName(code),
      })));
      api.get("/payroll/setup/packs").then(r => setPacks(r.data)).catch(()=>{});
    }
  }, [open]);

  useEffect(() => {
    if (anchor === "ctc") setTarget(1800000);
    else if (anchor === "monthly_gross") setTarget(150000);
    else if (anchor === "monthly_takehome") setTarget(120000);
    else setTarget(60000);
  }, [anchor]);

  // Seed customLines whenever pack or anchor changes
  useEffect(() => {
    const template = (PACK_LINES_BY_ANCHOR[pack] || {})[anchor] || [];
    setCustomLines(template.map(([code, kind, calc, val]) => ({
      code, kind, calculation_type: calc, value: val, include: true,
      name: prettyName(code),
    })));
  }, [pack, anchor]);

  // For Monthly Basic anchor, push the top amount into the BASIC/STIPEND row
  // so downstream % rules (HRA, PF) reverse-calculate live.
  useEffect(() => {
    if (anchor !== "monthly_basic") return;
    setCustomLines(prev => prev.map(l =>
      (l.code === "BASIC" || l.code === "STIPEND") && l.calculation_type === "fixed_monthly"
        ? { ...l, value: Number(target) || 0 }
        : l
    ));
  }, [anchor, target]);

  const applyLine = (idx, patch) => {
    setCustomLines(prev => prev.map((l,i) => i === idx ? { ...l, ...patch } : l));
  };
  const removeLine = (idx) => setCustomLines(prev => prev.filter((_,i) => i !== idx));
  const resetLines = () => {
    const template = (PACK_LINES_BY_ANCHOR[pack] || {})[anchor] || [];
    setCustomLines(template.map(([code, kind, calc, val]) => ({
      code, kind, calculation_type: calc, value: val, include: true, name: prettyName(code),
    })));
  };
  const addBlankLine = () => {
    setCustomLines(prev => [...prev, {
      code: "", kind: "earning", calculation_type: "fixed_monthly", value: 0, include: true, name: "",
    }]);
  };

  const activeLines = customLines.filter(l => l.include && (l.code || "").trim());

  const commit = async () => {
    // Validate: at least one earning; codes must be unique + upper-snake
    if (activeLines.length === 0) { toast.error("Add at least one component."); return; }
    const codes = activeLines.map(l => normalizeCode(l.code));
    if (new Set(codes).size !== codes.length) { toast.error("Component codes must be unique."); return; }
    setBusy(true);
    try {
      const payload = {
        pack,
        anchor: effectiveAnchor,
        target_amount: Number(effectiveTarget) || 0,
        custom_lines: activeLines.map(l => ({
          code: normalizeCode(l.code),
          name: l.name || prettyName(l.code),
          kind: l.kind,
          calculation_type: l.calculation_type,
          value: Number(l.value) || 0,
        })),
      };
      const { data } = await api.post("/payroll/setup/pack", payload);
      toast.success(`Payroll ready — ${data.created_components.length} components + "${data.structure.name}".`);
      onOpenChange(false);
      onComplete?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Setup failed");
    } finally { setBusy(false); }
  };

  // For take-home anchor, solve for the gross that yields this net, then
  // reuse the gross estimator. Solved gross is a memo so the table updates live.
  const solvedGross = useMemo(() => {
    if (anchor !== "monthly_takehome") return null;
    const tgt = Number(target) || 0;
    if (tgt <= 0) return 0;
    // Binary search on gross. Take-home is monotonic in gross so this converges fast.
    let lo = tgt, hi = tgt * 3.5;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      const rows = estimateLines({ anchor: "monthly_gross", target: mid, lines: activeLines });
      const th = sumBy(rows, "earning") - sumBy(rows, "deduction");
      if (th < tgt) lo = mid; else hi = mid;
    }
    return Math.round((lo + hi) / 2);
  }, [anchor, target, activeLines]);

  // For rendering, use the solved gross (or the user's raw target for other anchors).
  const effectiveAnchor = anchor === "monthly_takehome" ? "monthly_gross" : anchor;
  const effectiveTarget = anchor === "monthly_takehome" ? (solvedGross || 0) : target;
  const liveLines = useMemo(() => estimateLines({ anchor: effectiveAnchor, target: effectiveTarget, lines: activeLines }), [effectiveAnchor, effectiveTarget, activeLines]);
  const liveByCode = useMemo(() => Object.fromEntries(liveLines.map(l => [l.code, l.monthly])), [liveLines]);
  const packOptions = { ...packs, custom: CUSTOM_PACK };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-200 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-amber-500"/>
            Payroll setup wizard
            <span className="text-xs font-normal text-zinc-500 ml-2">Step {step} of 3</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              How does your company usually talk about salaries? Pick the one your offers/invoices are worded around — we&apos;ll wire the math accordingly.
            </p>
            <div className="grid grid-cols-3 gap-3 mt-3">
              {ANCHORS.map(a => (
                <button key={a.key} onClick={()=>setAnchor(a.key)} data-testid={`wiz-anchor-${a.key}`}
                  className={`text-left rounded-lg border-2 p-4 transition ${anchor===a.key?"border-zinc-950 bg-zinc-50":"border-zinc-200 hover:border-zinc-300"}`}>
                  <div className="text-2xl mb-1">{a.icon}</div>
                  <div className="font-medium">{a.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{a.example}</div>
                  <div className="text-[11px] text-zinc-500 mt-2 leading-tight">{a.hint}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              Pick a starter — we&apos;ll pre-fill components in the next step. You can add / remove / tweak everything there.
            </p>
            <div className="grid grid-cols-4 gap-3 mt-3">
              {Object.values(packOptions).map(p => (
                <button key={p.code} onClick={()=>setPack(p.code)} data-testid={`wiz-pack-${p.code}`}
                  className={`text-left rounded-lg border-2 p-4 transition ${pack===p.code?"border-zinc-950 bg-zinc-50":"border-zinc-200 hover:border-zinc-300"}`}>
                  <div className="text-2xl mb-1">{PACK_ICONS[p.code] || "📦"}</div>
                  <div className="font-medium">{p.label}</div>
                  <div className="text-xs text-zinc-500 mt-1 leading-snug">{p.description}</div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(p.components || []).slice(0,5).map(c => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
                    {(p.components || []).length > 5 && <span className="text-[10px] text-zinc-400">+{p.components.length-5}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            {/* Anchor amount — drives the reverse calculation live below */}
            <div className="bg-amber-50/70 border border-amber-200 rounded-md p-3 flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[220px]">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">
                  {anchor === "ctc" ? "Annual CTC" :
                   anchor === "monthly_gross" ? "Monthly Gross salary" :
                   anchor === "monthly_takehome" ? "Monthly Take-home (net-in-hand)" :
                   "Monthly Basic salary"} (₹)
                </Label>
                <Input type="number" value={target} onChange={e=>setTarget(e.target.value)}
                       className="h-11 text-lg font-mono mt-1" data-testid="wiz-anchor-amt"/>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {anchor === "monthly_takehome"
                    ? <>Enter what you want the employee to <b>actually receive</b> in bank. We&apos;ll solve backwards — Gross ~ {solvedGross ? inr(solvedGross) : "…"} — and populate every component.</>
                    : <>Type the amount your offer/contract mentions. The <b>Monthly ₹</b> column below reverse-calculates for every component using your rules.</>}
                </div>
              </div>
              <div className="text-xs text-zinc-600 min-w-[220px]">
                <div>Anchor: <b>{ANCHORS.find(a=>a.key===anchor)?.label}</b></div>
                <div>Pack: <b>{packOptions[pack]?.label}</b></div>
                <div>Selected: <b>{customLines.filter(l=>l.include && (l.code||"").trim()).length}</b> of {customLines.length}</div>
              </div>
              <Button variant="outline" size="sm" onClick={resetLines} className="gap-1.5" data-testid="wiz-reset">
                <ArrowClockwise size={12}/> Reset to pack defaults
              </Button>
            </div>

            <p className="text-xs text-zinc-500">
              Tick the components you want. Edit the calculation type and value on the right — the live <b>Monthly ₹</b> column shows what each component pays out based on the anchor above.
            </p>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-600 sticky top-0">
                  <tr>
                    <th className="p-2 text-left w-[36px]"></th>
                    <th className="p-2 text-left w-[110px]">Code</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left w-[110px]">Kind</th>
                    <th className="p-2 text-left w-[150px]">Calculation</th>
                    <th className="p-2 text-left w-[90px]">Value</th>
                    <th className="p-2 text-right w-[100px]">Monthly ₹</th>
                    <th className="p-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {customLines.map((l, i) => {
                    const computed = liveByCode[normalizeCode(l.code)] || 0;
                    return (
                    <tr key={i} className={`border-t ${l.include ? "" : "opacity-50"}`}>
                      <td className="p-2 text-center">
                        <input type="checkbox" checked={!!l.include} onChange={e=>applyLine(i,{include:e.target.checked})} data-testid={`wiz-line-inc-${i}`}/>
                      </td>
                      <td className="p-2"><Input value={l.code} onChange={e=>applyLine(i,{code:e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,"_")})} className="h-8 text-xs font-mono"/></td>
                      <td className="p-2"><Input value={l.name} onChange={e=>applyLine(i,{name:e.target.value})} className="h-8 text-xs"/></td>
                      <td className="p-2">
                        <Select value={l.kind} onValueChange={v=>applyLine(i,{kind:v})}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            {Object.entries(KIND_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Select value={l.calculation_type} onValueChange={v=>applyLine(i,{calculation_type:v})}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            {CALC_OPTIONS.map(c => <SelectItem key={c} value={c}>{CALC_LABEL[c]}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input type="number" value={l.value} onChange={e=>applyLine(i,{value:parseFloat(e.target.value)||0})}
                               disabled={l.calculation_type==="statutory" || l.calculation_type==="balance"}
                               className="h-8 text-xs"/>
                      </td>
                      <td className={`p-2 text-right font-mono ${l.kind === "deduction" ? "text-red-700" : l.kind === "employer_cost" ? "text-zinc-500" : "text-emerald-700"}`}>
                        {l.include && computed > 0 ? inr(computed) : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={()=>removeLine(i)} className="h-8 w-8 p-0"><Trash size={12}/></Button>
                      </td>
                    </tr>
                  );})}
                  {customLines.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-zinc-400 py-4 italic">No components yet — add one below.</td></tr>
                  )}
                </tbody>
                {liveLines.length > 0 && (
                  <tfoot className="bg-zinc-50 text-xs">
                    <tr className="border-t">
                      <td colSpan={6} className="p-2 text-right font-medium">Gross monthly (earnings)</td>
                      <td className="p-2 text-right font-mono font-medium">{inr(sumBy(liveLines,"earning"))}</td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="p-2 text-right text-red-700">Employee deductions</td>
                      <td className="p-2 text-right font-mono text-red-700">−{inr(sumBy(liveLines,"deduction"))}</td>
                      <td></td>
                    </tr>
                    <tr className="border-t">
                      <td colSpan={6} className="p-2 text-right font-medium text-emerald-700">Estimated take-home</td>
                      <td className="p-2 text-right font-mono font-medium text-emerald-700">{inr(sumBy(liveLines,"earning") - sumBy(liveLines,"deduction"))}</td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="p-2 text-right text-zinc-500">Employer cost (extra)</td>
                      <td className="p-2 text-right font-mono text-zinc-500">{inr(sumBy(liveLines,"employer_cost"))}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <Button variant="outline" size="sm" onClick={addBlankLine} className="gap-1.5" data-testid="wiz-add-line">
              <Plus size={12} weight="bold"/> Add component
            </Button>
            <div className="text-[11px] text-zinc-500 leading-tight">
              Tip: Set <b>Balance</b> on one earning (usually Special Allowance) so the numbers add up exactly to your CTC/Gross.
              Statutory rows auto-compute from your Statutory Config page — they don&apos;t need a value here.
            </div>
          </div>
        )}

        {step === 4 && false && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              Try it — enter a sample amount and preview the breakdown. This becomes your starter salary structure.
            </p>
            <div className="grid grid-cols-[220px,1fr] gap-4">
              <div>
                <Label className="text-xs">
                  {anchor === "ctc" ? "Annual CTC (₹)" : anchor === "monthly_gross" ? "Monthly Gross (₹)" : "Monthly Basic (₹)"}
                </Label>
                <Input type="number" value={target} onChange={e=>setTarget(e.target.value)} data-testid="wiz-target"/>
                <div className="text-xs text-zinc-500 mt-2 leading-snug">
                  Anchor: <b>{ANCHORS.find(a=>a.key===anchor)?.label}</b><br/>
                  Pack: <b>{packOptions[pack]?.label}</b><br/>
                  Components: <b>{activeLines.length}</b>
                </div>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 max-h-[340px] overflow-y-auto">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Live estimate — monthly breakdown</div>
                {liveLines.length === 0 && <div className="text-xs text-zinc-500 italic">Enter an amount to preview…</div>}
                {liveLines.map(l => (
                  <div key={l.code} className={`flex justify-between text-xs py-1 ${l.kind === "deduction" ? "text-red-700" : l.kind === "employer_cost" ? "text-zinc-500" : ""}`}>
                    <span>{l.name} <span className="text-zinc-400">({l.code})</span></span>
                    <span className="font-mono">{inr(l.monthly)}</span>
                  </div>
                ))}
                {liveLines.length > 0 && (
                  <>
                    <hr className="my-2 border-zinc-200"/>
                    <SumRow label="Gross monthly (earnings)" value={sumBy(liveLines,"earning")} bold/>
                    <SumRow label="Employee deductions" value={-sumBy(liveLines,"deduction")} className="text-red-700"/>
                    <SumRow label="Estimated take-home" value={sumBy(liveLines,"earning") - sumBy(liveLines,"deduction")} bold className="text-emerald-700"/>
                    <SumRow label="Employer cost (extra)" value={sumBy(liveLines,"employer_cost")} className="text-zinc-500"/>
                  </>
                )}
                <div className="text-[10px] text-zinc-400 mt-3 leading-tight">
                  Estimate uses default rates (PF 12% on Basic ceiling ₹15k, ESIC applied if gross ≤ ₹21k, PT ₹200/mo). Real payroll runs use your configured Statutory Config.
                </div>
              </div>
            </div>
          </div>
        )}

        </div>

        <DialogFooter className="justify-between px-6 py-4 border-t border-zinc-200 bg-white shrink-0">
          <div>
            {step > 1 && (
              <Button variant="ghost" onClick={()=>setStep(step-1)} className="gap-1.5" data-testid="wiz-back">
                <ArrowLeft size={14}/> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={()=>onOpenChange(false)}>Skip and do manually</Button>
            {step < 3 && (
              <Button onClick={()=>setStep(step+1)} data-testid="wiz-next">Continue</Button>
            )}
            {step === 3 && (
              <Button onClick={commit} disabled={busy || !Number(target)} className="gap-1.5" data-testid="wiz-commit">
                <CheckCircle size={14} weight="bold"/>{busy ? "Setting up…" : "Create it — I’m in"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SumRow({ label, value, bold, className="" }) {
  return (
    <div className={`flex justify-between text-xs py-0.5 ${bold?"font-medium":""} ${className}`}>
      <span>{label}</span>
      <span className="font-mono">{inr(value)}</span>
    </div>
  );
}
function sumBy(lines, kind) {
  return lines.filter(l => l.kind === kind).reduce((a,l)=>a + (l.monthly || 0), 0);
}
function prettyName(code) {
  if (!code) return "";
  const KNOWN = { BASIC:"Basic Salary", HRA:"House Rent Allowance", CONV:"Conveyance",
    MEDICAL:"Medical Allowance", LTA:"Leave Travel Allowance", SPECIAL:"Special Allowance",
    STIPEND:"Stipend", PF:"Employee PF", EMPF:"Employer PF", EPS:"EPS (from employer)",
    EDLI:"EDLI", PF_ADMIN:"PF Admin", ESIC:"Employee ESIC", EESIC:"Employer ESIC",
    PT:"Professional Tax", LWF:"Employee LWF", ELWF:"Employer LWF", GRAT:"Gratuity", TDS:"TDS" };
  return KNOWN[code] || code.replace(/_/g," ").replace(/\b\w/g, c=>c.toUpperCase());
}
function normalizeCode(code) {
  return (code || "").toString().toUpperCase().replace(/[^A-Z0-9_]/g,"_").replace(/^_+|_+$/g,"");
}

// ---------- client-side estimator (reads the user-tuned lines) ----------
function estimateLines({ anchor, target, lines }) {
  const t = Number(target) || 0;
  if (!t || !lines?.length) return [];
  const monthlyCtc = anchor === "ctc" ? t/12 : anchor === "monthly_gross" ? t*1 : t*2.2;
  const monthlyGross = anchor === "monthly_gross" ? t : anchor === "ctc" ? t/12 : t*1.9;

  // Sort so gross-dependent lines compute after fixed/statutory but before balance
  const order = ["fixed_monthly","pct_of_ctc","pct_of_gross","pct_of_basic","statutory","balance"];
  const sorted = [...lines].sort((a,b) => order.indexOf(a.calculation_type) - order.indexOf(b.calculation_type));

  const out = [];
  const findAmount = (code) => (out.find(x => x.code === code) || {}).monthly || 0;
  const grossSoFar = () => out.filter(x => x.kind === "earning").reduce((a,x)=>a+x.monthly,0);

  for (const l of sorted) {
    const code = normalizeCode(l.code);
    let m = 0;
    if (l.calculation_type === "fixed_monthly") m = Number(l.value) || 0;
    else if (l.calculation_type === "pct_of_basic") m = (findAmount("BASIC") || 0) * (Number(l.value)||0) / 100;
    else if (l.calculation_type === "pct_of_ctc") m = monthlyCtc * (Number(l.value)||0) / 100;
    else if (l.calculation_type === "pct_of_gross") m = monthlyGross * (Number(l.value)||0) / 100;
    else if (l.calculation_type === "balance") m = Math.max(0, monthlyGross - grossSoFar());
    else if (l.calculation_type === "statutory") m = estimateStatutory(code, findAmount("BASIC"), grossSoFar());
    if (m > 0) out.push({ code, name: prettyName(code), kind: l.kind, monthly: Math.round(m*100)/100 });
  }
  return out;
}

function estimateStatutory(code, basic, gross) {
  const pfWage = Math.min(basic || 0, 15000);
  switch (code) {
    case "PF": case "EMPF": return pfWage * 0.12;
    case "EPS": return pfWage * 0.0833;
    case "EDLI": return pfWage * 0.005;
    case "PF_ADMIN": return pfWage * 0.005;
    case "ESIC": return gross <= 21000 ? gross * 0.0075 : 0;
    case "EESIC": return gross <= 21000 ? gross * 0.0325 : 0;
    case "PT": return 200;
    case "GRAT": return (basic || 0) * 0.0481;
    default: return 0;
  }
}
